import api from '@/services/base/BaseApi';
import { handleApiError } from '@/services/base/handleApiError';
import {
  ScheduledBankPaymentGroupStateEnum,
  ScheduledPaymentIntervalTypeEnum,
  TScheduledPaymentIntervalType
} from '@/features/payments/types/BatchPayment';
import {
  PaymentRowKindEnum,
  TPaymentListItem
} from '@/features/payments/manage/types/ManagePayments';

// Mirrors the backend's ScheduledBankPaymentState (@JsonValue) - the *last transaction attempt's*
// outcome, not a scheduling/approval state. Absent means the payment hasn't been attempted yet,
// which is exactly the "still upcoming" case this feature needs for the Scheduled tab.
//
// RsScheduledBankPayment is annotated @JsonInclude(NON_NULL), so a null lastPaymentState is
// omitted from the response entirely rather than sent as `null` - it arrives here as `undefined`,
// not `null`. The field is typed optional (not `| null`) to make that explicit.
type TLastPaymentState = 'completed' | 'failed';

interface IRsScheduledBankPayment {
  id: string;
  walletId: string;
  customerId: string;
  paymentDate: string;
  paymentIntervalType: TScheduledPaymentIntervalType | null;
  paymentIntervalValue: number | null;
  amount: number;
  payeeName: string;
  payeeBankAccount: string;
  payeeReference?: string | null;
  payeeParticulars?: string | null;
  payeeAnalysis?: string | null;
  payerReference?: string | null;
  payerParticulars?: string | null;
  payerAnalysis?: string | null;
  lastPaymentState?: TLastPaymentState;
  transactionId?: string | null;
  createdAt: string;
  isActive: boolean;
}

// v2, not v1: v1's mapper nulls out paymentIntervalType for one-off payments, which breaks the
// "Scheduled" vs "Recurring scheduled" distinction the Type column needs.
const transformScheduledPayment = (payment: IRsScheduledBankPayment): TPaymentListItem => ({
  id: payment.id,
  name: payment.payeeName,
  kind: PaymentRowKindEnum.SCHEDULED_PAYMENT,
  paymentIntervalType: payment.paymentIntervalType ?? ScheduledPaymentIntervalTypeEnum.ONE_OFF,
  // Needed alongside paymentIntervalType for the Scheduled date column's recurrence annotation
  // (see buildRecurringDateText) - e.g. distinguishing "every week" from "every 3 weeks".
  paymentIntervalValue: payment.paymentIntervalValue ?? undefined,
  // Not exposed by the backend for individual payments yet - rendered as '-' in the table.
  createdDate: payment.createdAt,
  scheduledDate: payment.paymentDate,
  totalAmount: payment.amount,
  walletId: payment.walletId,
  // lastPaymentState is the *last attempt's* outcome, not a scheduling state - undefined (never
  // attempted) and 'completed' (most recent recurring run succeeded, more occurrences left) both
  // still belong in the Scheduled tab, same as an APPROVED group (deletable up to the same
  // cutoff). Only a genuinely one-off payment reaches 'completed' with isActive=false, and that
  // row is never returned by the backend's isActive-filtered list to begin with. Failed: reuses
  // FAILED so it lands in Action required and gets the same retry/delete controls as a failed
  // group - every state-keyed lookup (status label/style, ACTION_BY_STATE) works unchanged
  // without a new state value either way.
  state:
    payment.lastPaymentState === 'failed'
      ? ScheduledBankPaymentGroupStateEnum.FAILED
      : ScheduledBankPaymentGroupStateEnum.APPROVED,
  totalPayments: 1,
  createdByCustomerId: payment.customerId,
  paymentDetail: {
    id: payment.id,
    payeeName: payment.payeeName,
    payeeBankAccount: payment.payeeBankAccount,
    payeeParticulars: payment.payeeParticulars,
    payeeAnalysis: payment.payeeAnalysis,
    payeeReference: payment.payeeReference,
    payerParticulars: payment.payerParticulars,
    payerAnalysis: payment.payerAnalysis,
    payerReference: payment.payerReference
  }
});

/**
 * Individual scheduled payments that never belonged to a multi-payee batch group (see
 * EScheduledBankPayment.scheduledBankPaymentGroup - genuinely nullable, not a trivial
 * auto-created group). Both "not yet attempted" and "last (recurring) run completed" land in the
 * Scheduled tab, "failed" in Action required (see transformScheduledPayment above) -
 * lastPaymentState alone can't tell a finished one-off from an active recurring payment, so it's
 * never used to filter here. `isActive` is still checked defensively even though the backend's
 * own list query is already scoped to it - costs nothing when the backend contract holds, and
 * guards against a stale/inactive row slipping through if it ever doesn't.
 *
 * Callers pass both accountId and businessId unconditionally (whatever useAuth currently has) -
 * businessId takes priority when present since accountId only applies to a personal account; the
 * caller doesn't need to know which one is relevant for the current viewer.
 */
export const getScheduledPayments = async (
  accountId?: string,
  businessId?: string
): Promise<TPaymentListItem[]> => {
  const params = new URLSearchParams();
  if (businessId) {
    params.append('businessId', businessId);
  } else if (accountId) {
    params.append('accountId', accountId);
  }
  const responseBody = await api
    .get<IRsScheduledBankPayment[]>(`api/v1/bank/scheduled-payments?${params.toString()}`)
    .catch(handleApiError);

  return responseBody.data.filter((payment) => payment.isActive).map(transformScheduledPayment);
};

/** DELETE endpoint exists per-payment (unlike groups, which don't have one yet). */
export const deleteScheduledPayment = (paymentId: string) =>
  api
    .delete<unknown, undefined>(`api/v1/bank/scheduled-payments/${paymentId}`)
    .catch(handleApiError);

/** Retries a failed individual payment. Returns no body - the caller can't know the new outcome. */
export const retryScheduledPayment = (paymentId: string) =>
  api
    .post<unknown, null>(`api/v1/bank/scheduled-payments/${paymentId}/retry`, null)
    .catch(handleApiError);
